import React, { useState, useEffect, useRef } from 'react';
import { UserProfile } from '../types';
import { ArrowRight, ArrowLeft, Check, User, AlertCircle, Edit2, Sparkles } from 'lucide-react';

interface QuizProps {
  onComplete: (profile: UserProfile) => void;
  initialData?: UserProfile | null;
}

interface Question {
  id: keyof UserProfile;
  question: string;
  options: { label: string; value: string }[];
  multiSelect?: boolean;
  condition?: (currentAnswers: Partial<UserProfile>) => boolean;
}

const QUESTIONS: Question[] = [
  {
    id: 'sourceCountry',
    question: "Where are you moving from?",
    options: [
      { label: "United States 🇺🇸", value: "US" },
      { label: "United Kingdom 🇬🇧", value: "UK" },
      { label: "Germany 🇩🇪", value: "Germany" },
      { label: "Other 🌍", value: "Other" }
    ]
  },
  {
    id: 'familyType',
    question: "What's your family composition?",
    options: [
      { label: "Single professional", value: "single" },
      { label: "Couple without kids", value: "couple_no_kids" },
      { label: "Family with kids", value: "family_with_kids" }
    ]
  },
  {
    id: 'childrenAges',
    question: "What are your children's ages?",
    options: [
      { label: "Preschool (2-5)", value: "preschool" },
      { label: "Elementary (6-10)", value: "elementary" },
      { label: "Middle school (11-14)", value: "middle_school" },
      { label: "High school (15-18)", value: "high_school" }
    ],
    multiSelect: true,
    condition: (answers) => answers.familyType === "family_with_kids"
  },
  {
    id: 'workLocation',
    question: "Where will you be working in Pune?",
    options: [
      { label: "Hinjewadi IT Park", value: "hinjewadi" },
      { label: "Kharadi/EON IT Park", value: "kharadi" },
      { label: "Magarpatta", value: "magarpatta" },
      { label: "Work from home", value: "wfh" },
      { label: "Not sure yet", value: "unsure" }
    ]
  },
  {
    id: 'timeline',
    question: "What's your timeline for moving?",
    options: [
      { label: "Less than 6 months", value: "less_than_6_months" },
      { label: "6-12 months", value: "6_to_12_months" },
      { label: "1-2 years", value: "1_to_2_years" },
      { label: "Just exploring", value: "exploring" }
    ]
  },
  {
    id: 'budget',
    question: "What's your budget range for housing?",
    options: [
      { label: "Rent: ₹30-50K/month", value: "rent_30_50k" },
      { label: "Rent: ₹50-80K/month", value: "rent_50_80k" },
      { label: "Buy: ₹1-2 Cr", value: "buy_1_2_cr" },
      { label: "Buy: ₹2-4 Cr", value: "buy_2_4_cr" },
      { label: "Buy: ₹4+ Cr", value: "buy_4_plus_cr" }
    ]
  }
];

const Quiz: React.FC<QuizProps> = ({ onComplete, initialData }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSummary, setIsSummary] = useState(!!initialData?.isPersonalized); // Skip to summary only if editing a personalized profile
  const [answers, setAnswers] = useState<Partial<UserProfile>>(initialData || {
    childrenAges: []
  });
  const [shake, setShake] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Timer ref for debouncing auto-advance
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Filter questions dynamically based on previous answers (Conditional Logic)
  const activeQuestions = QUESTIONS.filter(q =>
    !q.condition || q.condition(answers)
  );

  // Ensure current step is valid if questions disappear dynamically
  useEffect(() => {
    if (currentStep >= activeQuestions.length && !isSummary) {
      setCurrentStep(activeQuestions.length - 1);
    }
  }, [answers, activeQuestions.length, currentStep, isSummary]);

  const currentQuestion = activeQuestions[currentStep];

  const triggerError = (msg: string) => {
    setShake(true);
    setErrorMsg(msg);
    setTimeout(() => setShake(false), 500);
    // Clear error message after 3 seconds
    setTimeout(() => setErrorMsg(null), 3000);
  };

  const handleOptionSelect = (value: string) => {
    // Clear error on select
    if (errorMsg) setErrorMsg(null);

    if (currentQuestion.multiSelect) {
      const currentList = (answers[currentQuestion.id] as string[]) || [];
      const newList = currentList.includes(value)
        ? currentList.filter(item => item !== value)
        : [...currentList, value];

      setAnswers({ ...answers, [currentQuestion.id]: newList });
    } else {
      // Single Select Logic
      setAnswers(prev => {
        const newAnswers = { ...prev, [currentQuestion.id]: value };
        // Reset children ages if changing away from family with kids
        if (currentQuestion.id === 'familyType' && value !== 'family_with_kids') {
           newAnswers.childrenAges = [];
        }
        return newAnswers;
      });

      // Clear any pending navigation
      if (timerRef.current) clearTimeout(timerRef.current);

      // Debounced Auto-advance
      timerRef.current = setTimeout(() => {
        if (currentStep < activeQuestions.length - 1) {
          setCurrentStep(prev => prev + 1);
        } else {
          setIsSummary(true);
        }
      }, 250);
    }
  };

  const hasSelection = () => {
    if (!currentQuestion) return false;
    const value = answers[currentQuestion.id];
    return Array.isArray(value) ? value.length > 0 : !!value;
  };

  const handleNext = () => {
    // Validation
    if (!hasSelection()) {
      triggerError("Please select an option to continue");
      return;
    }

    if (currentStep < activeQuestions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setIsSummary(true);
    }
  };

  const handleBack = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (isSummary) {
      setIsSummary(false);
    } else if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleEdit = (questionId: keyof UserProfile) => {
    const index = activeQuestions.findIndex(q => q.id === questionId);
    if (index !== -1) {
      setCurrentStep(index);
      setIsSummary(false);
    }
  };

  const handleFinalSubmit = () => {
    const finalProfile: UserProfile = {
      ...answers as UserProfile,
      createdAt: new Date().toISOString()
    };
    onComplete(finalProfile);
  };

  // Helper to get display label from value
  const getLabel = (questionId: keyof UserProfile, val: string) => {
    const q = QUESTIONS.find(q => q.id === questionId);
    return q?.options.find(o => o.value === val)?.label || val;
  };

  // Progress calculation
  const progress = isSummary ? 100 : ((currentStep + 1) / activeQuestions.length) * 100;

  // --- VIEW: SUMMARY SCREEN ---
  if (isSummary) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <div className="max-w-xl w-full bg-white rounded-2xl shadow-xl overflow-hidden animate-fadeIn">
          <div className="bg-indigo-600 p-6 text-white text-center">
            <Sparkles className="mx-auto mb-3" size={32} />
            <h2 className="text-2xl font-bold">{initialData?.isPersonalized ? 'Update Preferences' : "You're All Set!"}</h2>
            <p className="text-indigo-100">{initialData?.isPersonalized ? 'Review your changes below' : 'Review your preferences before we personalize your plan.'}</p>
          </div>

          <div className="p-6 space-y-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
            {activeQuestions.map((q) => {
              const val = answers[q.id];
              const displayValue = Array.isArray(val)
                ? (val.length > 0 ? val.map(v => getLabel(q.id, v)).join(", ") : "None")
                : getLabel(q.id, val as string);

              return (
                <div key={q.id} className="flex justify-between items-center p-3 bg-slate-50 rounded-lg border border-slate-100 group hover:border-indigo-100 transition">
                  <div className="flex-1">
                    <p className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">{q.question}</p>
                    <p className="font-medium text-slate-800">{displayValue}</p>
                  </div>
                  <button
                    onClick={() => handleEdit(q.id)}
                    className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-full transition"
                    title="Edit answer"
                  >
                    <Edit2 size={16} />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="p-6 border-t border-slate-100 bg-slate-50">
             <button
               onClick={handleFinalSubmit}
               className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-xl shadow-lg shadow-indigo-200 transition-all hover:scale-[1.02] flex items-center justify-center gap-2"
             >
               {initialData?.isPersonalized ? 'Save Changes' : 'Personalize My Plan'} <ArrowRight size={20} />
             </button>
             <button
               onClick={handleBack}
               className="w-full mt-3 text-slate-500 font-medium text-sm hover:text-slate-800"
             >
               Back to Quiz
             </button>
          </div>
        </div>
      </div>
    );
  }

  // --- VIEW: QUESTION SCREEN ---
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className={`max-w-xl w-full bg-white rounded-2xl shadow-xl overflow-hidden ${shake ? 'animate-shake' : ''}`}>
        {/* Progress Bar */}
        <div className="h-2 bg-slate-100">
          <div
            className="h-full bg-indigo-600 transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-8">
            <span className="text-xs font-bold text-indigo-600 tracking-wider uppercase">
              Step {currentStep + 1} of {activeQuestions.length}
            </span>
            {currentStep > 0 && (
              <button
                onClick={handleBack}
                className="text-slate-400 hover:text-slate-600 transition"
              >
                <ArrowLeft size={20} />
              </button>
            )}
          </div>

          {/* Question */}
          <h2 className="text-2xl font-bold text-slate-800 mb-6 animate-fadeIn min-h-[64px]">
            {currentQuestion?.question}
          </h2>

          {/* Options */}
          <div className="space-y-3 mb-8">
            {currentQuestion?.options.map((option) => {
              const isSelected = currentQuestion.multiSelect
                ? (answers[currentQuestion.id] as string[])?.includes(option.value)
                : answers[currentQuestion.id] === option.value;

              return (
                <button
                  key={option.value}
                  onClick={() => handleOptionSelect(option.value)}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center justify-between group
                    ${isSelected
                      ? 'border-indigo-600 bg-indigo-50 text-indigo-700 shadow-sm'
                      : 'border-slate-100 hover:border-indigo-200 hover:bg-slate-50 text-slate-600'
                    }`}
                >
                  <span className="font-medium text-lg">{option.label}</span>
                  {isSelected && (
                    <div className="bg-indigo-600 text-white rounded-full p-1">
                      <Check size={14} strokeWidth={3} />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Error Message */}
          {errorMsg && (
            <div className="mb-4 flex items-center gap-2 text-red-500 text-sm font-medium animate-fadeIn">
              <AlertCircle size={16} />
              {errorMsg}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end">
            <button
              onClick={handleNext}
              className={`flex items-center gap-2 px-8 py-3 rounded-xl font-bold text-white transition-all transform
                ${hasSelection()
                  ? 'bg-indigo-600 hover:bg-indigo-700 hover:translate-x-1 shadow-lg shadow-indigo-200'
                  : 'bg-slate-300 hover:bg-slate-400'
                }`}
            >
              Next
              <ArrowRight size={20} />
            </button>
          </div>
        </div>

        {/* Info Footer */}
        <div className="bg-slate-50 px-8 py-4 border-t border-slate-100 flex items-center gap-3 text-slate-500 text-sm">
          <User size={16} />
          <span>We'll use this to customize your relocation guide.</span>
        </div>
      </div>
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-4px); }
          20%, 40%, 60%, 80% { transform: translateX(4px); }
        }
        .animate-shake {
          animation: shake 0.4s cubic-bezier(.36,.07,.19,.97) both;
        }
      `}</style>
    </div>
  );
};

export default Quiz;
